export interface ReportSheetInfo {
  worksheet: Excel.Worksheet;
  label: string;
  name: string;
}

export const WORKSHEET_CUSTOM_PROPERTIES = {
  REPORT_TYPE: "Report Type", // ex. "Detailed Estimate", "Area Sheet", "GCGR", etc.
  IS_TEMPLATE: "Is Template", // ex. "true" or "false"
  LABEL: "Label", // Optional label for additional context like building name, etc.
};

// NIT: Refactor to use worksheet cache
export const getWorksheetCustomProperties = async (
  context: Excel.RequestContext,
  worksheet: Excel.Worksheet
): Promise<Map<string, string>> => {
  const customProperties = worksheet.customProperties;
  customProperties.load("items");
  await context.sync();

  const sheetPropMap = new Map<string, string>();

  for (const prop of customProperties.items) {
    sheetPropMap.set(prop.key, prop.value);
  }

  return sheetPropMap;
};

export const getTemplateByReportTypeOrNull = async (
  context: Excel.RequestContext,
  reportType: string
): Promise<Excel.Worksheet | null> => {
  const workbook = context.workbook;
  const worksheets = workbook.worksheets;
  worksheets.load("items/name");
  await context.sync();

  for (const worksheet of worksheets.items) {
    const worksheetObj = worksheets.getItem(worksheet.name);
    const customProperties = worksheetObj.customProperties;
    customProperties.load("items");
    await context.sync();

    // Check if this worksheet has both required properties
    let isTemplate = false;
    let hasMatchingReportName = false;

    for (const prop of customProperties.items) {
      if (prop.key === WORKSHEET_CUSTOM_PROPERTIES.IS_TEMPLATE && prop.value == "true") {
        isTemplate = true;
      }
      if (prop.key === WORKSHEET_CUSTOM_PROPERTIES.REPORT_TYPE && prop.value === reportType) {
        hasMatchingReportName = true;
      }
    }

    // If both conditions are met, return this worksheet
    if (isTemplate && hasMatchingReportName) {
      return worksheet;
    }
  }

  return null;
};

export const getSheetsByReportType = async (
  context: Excel.RequestContext,
  reportType: string
): Promise<ReportSheetInfo[]> => {
  try {
    const workbook = context.workbook;
    const worksheets = workbook.worksheets;
    worksheets.load("items/name");
    await context.sync();

    const matchingWorksheets: ReportSheetInfo[] = [];

    for (const worksheet of worksheets.items) {
      const worksheetObj = worksheets.getItem(worksheet.name);
      const customProperties = worksheetObj.customProperties;
      customProperties.load("items");
      worksheet.load("name");
      await context.sync();

      let isTemplate = false;
      let hasMatchingReportType = false;

      for (const prop of customProperties.items) {
        if (prop.key === WORKSHEET_CUSTOM_PROPERTIES.IS_TEMPLATE && prop.value === "true") {
          isTemplate = true;
        }
        if (prop.key === WORKSHEET_CUSTOM_PROPERTIES.REPORT_TYPE && prop.value === reportType) {
          hasMatchingReportType = true;
        }
      }

      if (hasMatchingReportType && !isTemplate) {
        const label =
          customProperties.items.find((prop) => prop.key === WORKSHEET_CUSTOM_PROPERTIES.LABEL)?.value || "";
        const name = worksheet.name;
        matchingWorksheets.push({ worksheet, label, name });
      }
    }

    return matchingWorksheets;
  } catch (error) {
    console.error("Error checking for report sheets:", error);
    throw error;
  }
};

export const checkIfSheetIsTemplate = async (
  context: Excel.RequestContext,
  worksheet: Excel.Worksheet
): Promise<boolean> => {
  const customProperties = worksheet.customProperties;
  customProperties.load("items");
  await context.sync();

  for (const prop of customProperties.items) {
    if (prop.key === WORKSHEET_CUSTOM_PROPERTIES.IS_TEMPLATE && prop.value == "true") {
      return true;
    }
  }

  return false;
};

export const checkIfSheetWithReportTypeExists = async (
  context: Excel.RequestContext,
  reportType: string
): Promise<boolean> => {
  try {
    const workbook = context.workbook;
    const worksheets = workbook.worksheets;
    worksheets.load("items/name");
    await context.sync();

    for (const worksheet of worksheets.items) {
      const worksheetObj = worksheets.getItem(worksheet.name);
      const customProperties = worksheetObj.customProperties;
      customProperties.load("items");
      await context.sync();

      let isTemplate = false;
      let hasMatchingReportType = false;

      for (const prop of customProperties.items) {
        if (prop.key === WORKSHEET_CUSTOM_PROPERTIES.IS_TEMPLATE && prop.value === "true") {
          isTemplate = true;
        }
        if (prop.key === WORKSHEET_CUSTOM_PROPERTIES.REPORT_TYPE && prop.value === reportType) {
          hasMatchingReportType = true;
        }
      }

      if (hasMatchingReportType && !isTemplate) {
        return true;
      }
    }

    return false;
  } catch (error) {
    if (error instanceof OfficeExtension.Error) {
      console.error("Excel error in checkIfSheetWithReportTypeExists:", error.debugInfo);
    } else {
      console.error("Error in checkIfSheetWithReportTypeExists:", error);
    }
    return false;
  }
};
